import { useEffect, useState, useMemo } from "react";
import { api, money } from "@/lib/api";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Search, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const LEAD_LABEL = {
  pre_foreclosure: "Pre-Foreclosures",
  tax_delinquent: "Tax Delinquent",
  probate: "Probate",
  vacant: "Vacant Houses",
  absentee_owner: "Absentee Owners",
};
const LEAD_CHIPS = [
  { key: "pre_foreclosure", label: "Pre-Foreclosure" },
  { key: "tax_delinquent", label: "Tax Delinquent" },
  { key: "probate", label: "Probate" },
  { key: "vacant", label: "Vacant" },
  { key: "absentee_owner", label: "Absentee" },
];

export default function Properties() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter"); // 'hot' | null
  const leadType = searchParams.get("lead_type"); // one of the 5 lead source keys
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = leadType ? { lead_type: leadType } : {};
    api
      .get("/properties", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadType]);

  const filtered = useMemo(() => {
    let list = items;
    if (filter === "hot") {
      list = list.filter((p) => (p.motivation_score ?? 0) >= 70);
    }
    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (p) =>
          (p.address || "").toLowerCase().includes(term) ||
          (p.owner_name || "").toLowerCase().includes(term),
      );
    }
    return list;
  }, [items, q, filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this property? This cannot be undone.")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="properties-page">
      <div className="flex items-end justify-between gap-4 mb-6 reveal flex-wrap">
        <div>
          <span className="app-eyebrow">/ Portfolio</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">
            {filter === "hot"
              ? "Hot Leads"
              : leadType
                ? LEAD_LABEL[leadType] || "Properties"
                : "Properties"}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {filter === "hot"
              ? "Properties with motivation score 70+ — call these first."
              : leadType
                ? `Filtered to ${LEAD_LABEL[leadType] || leadType} only.`
                : "All addresses you've saved. Click any row to open the full deal analysis (no API credits used)."}
          </p>
          {(filter === "hot" || leadType) && (
            <button
              onClick={() => setSearchParams({})}
              data-testid="clear-filter-button"
              className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              <X size={12} /> Clear filter · show all
            </button>
          )}
        </div>
        <Link
          to="/lookup"
          className="inline-flex items-center gap-2 bg-foreground text-background px-4 h-10 rounded-sm text-sm font-medium hover:bg-foreground/90 transition-colors"
          data-testid="add-property-button"
        >
          <Plus size={14} /> New Analysis
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 reveal reveal-2" data-testid="lead-chips">
        {LEAD_CHIPS.map((c) => (
          <button
            key={c.key}
            data-testid={`chip-${c.key}`}
            onClick={() =>
              setSearchParams(leadType === c.key ? {} : { lead_type: c.key })
            }
            className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
              leadType === c.key
                ? "bg-foreground text-background border-foreground"
                : "bg-white text-muted-foreground border-border hover:border-foreground/40"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="relative max-w-md mb-4 reveal reveal-2">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          placeholder="Search by address or owner…"
          className="pl-10 h-11 rounded-sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="properties-search"
        />
      </div>

      <div className="card-flat reveal reveal-3">
        <div className="overflow-x-auto">
          <table className="w-full data-table text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3">Address</th>
                <th className="text-left px-5 py-3">Owner</th>
                <th className="text-right px-5 py-3">ARV</th>
                <th className="text-right px-5 py-3">Equity</th>
                <th className="text-right px-5 py-3">Score</th>
                <th className="text-right px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-muted-foreground">
                    {items.length === 0
                      ? "No properties yet. Start with Property Lookup."
                      : "No matches for that search."}
                  </td>
                </tr>
              )}
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`property-row-${p.id}`}
                  className="border-b border-border hover:bg-secondary/60 cursor-pointer transition-colors"
                  onClick={() => nav(`/lookup?id=${p.id}`)}
                >
                  <td className="px-5 py-3 truncate max-w-[320px]">{p.address}</td>
                  <td className="px-5 py-3 text-muted-foreground">
                    {p.owner_name || "—"}
                  </td>
                  <td className="px-5 py-3 text-right font-mono">
                    {money(p.arv)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-[hsl(var(--success))]">
                    {p.equity ? money(p.equity) : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-sm text-xs font-semibold ${
                        p.motivation_score >= 70
                          ? "bg-red-50 text-destructive"
                          : p.motivation_score >= 40
                            ? "bg-amber-50 text-[hsl(var(--warning))]"
                            : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {p.motivation_score ?? 0}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(p.id);
                      }}
                      className="text-muted-foreground hover:text-destructive p-1"
                      data-testid={`delete-property-${p.id}`}
                      aria-label="delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && items.length > 0 && (
          <div className="px-5 py-3 border-t border-border text-xs text-muted-foreground">
            Showing {filtered.length} of {items.length}
          </div>
        )}
      </div>
    </div>
  );
}
